import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { merge } from 'rxjs';
import { CustomValidator } from 'src/shared/validation';
import { toggleEditMode } from '../../../shared/utils';
import {HeimvorteilGebaeudeart, HeimvorteilProduktart} from '../../../shared/enum';
import { IHvDaten } from '../../models/hvdaten';
import { TranslateService } from '@ngx-translate/core';
import { PopupService } from '../../service/popup.service';

@Component({
  selector: 'app-verbaute-flaeche',
  templateUrl: './verbaute-flaeche.component.html',
  styleUrls: ['./verbaute-flaeche.component.scss']
})
export class VerbauteFlaecheComponent implements OnInit, OnChanges {

  @Input()
  parentFormGroupDirective: FormGroupDirective;

  @Input()
  formGroup: UntypedFormGroup;

  @Input()
  showGroup: boolean;

  @Input()
  produktart: HeimvorteilProduktart;

  @Input()
  gebaeudeart: HeimvorteilGebaeudeart;

  @Output()
  calculateRatePendingEvent = new EventEmitter<void>();

  stockwerke = 1;

  hasMansarde = false;

  hasKeller = false;

  produktarten = HeimvorteilProduktart;

  constructor(public translateService: TranslateService, public popupService: PopupService) { }

  static createFormGroup(fb: UntypedFormBuilder) {
    return fb.group({
      mansardeFlaecheQm: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(1), Validators.max(200)]),
      zweiterStockFlaecheQm: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(1), Validators.max(200)]),
      ersterStockFlaecheQm: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(1), Validators.max(200)]),
      erdgeschossFlaecheQm: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(1), Validators.max(200)]),
      kellerFlaecheQm: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(1), Validators.max(200)]),
      wohnkellerFlaecheQm: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(0), CustomValidator.KleinerGleichKeller])
    });
  }

  ngOnInit(): void {
    this.onValueChanged();
    this.toggleEditMode();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.showGroup && changes.showGroup.currentValue !== changes.showGroup.previousValue) {
      Promise.resolve().then(() => this.toggleEditMode());
    }
    if ((changes.produktart && changes.produktart.currentValue !== changes.produktart.previousValue) ||
      (changes.gebaeudeart && changes.gebaeudeart.currentValue !== changes.gebaeudeart.previousValue)
    ) {
      Promise.resolve().then(() => this.toggleEditMode());
    }
  }

  get image () {
    const suffix = this.hasMansarde && this.hasKeller ? 'both' : this.hasMansarde ? 'top' : this.hasKeller ? 'bot' : 'none';
    return 'assets/img/hv/eigenheim builder/Haus_' + this.stockwerke + '_' + suffix + '.png';
  }

  get mansardeFlaecheQm() { return this.formGroup.get('mansardeFlaecheQm'); }
  get zweiterStockFlaecheQm() { return this.formGroup.get('zweiterStockFlaecheQm'); }
  get ersterStockFlaecheQm() { return this.formGroup.get('ersterStockFlaecheQm'); }
  get erdgeschossFlaecheQm() { return this.formGroup.get('erdgeschossFlaecheQm'); }
  get kellerFlaecheQm() { return this.formGroup.get('kellerFlaecheQm'); }
  get wohnkellerFlaecheQm() { return this.formGroup.get('wohnkellerFlaecheQm'); }

  get showMansardeFlaecheQm() {
    return this.showGroup && this.hasMansarde;
  }
  get showZweiterStockFlaecheQm() {
    return this.showGroup && this.stockwerke >= 3;
  }
  get showErsterStockFlaecheQm() {
    return this.showGroup && this.stockwerke >= 2;
  }
  get showErdgeschossFlaecheQm() {
    return this.showGroup;
  }
  get showKellerFlaecheQm() {
    return this.showGroup && this.hasKeller;
  }
  get showWohnKellerFlaecheQm() {
    return this.showGroup && this.hasKeller && this.produktart !== HeimvorteilProduktart.Eigenheim ? true : false;
  }

  addStockwerk() {
    if(this.stockwerke < 3) {
      this.stockwerke++;
      this.toggleEditMode();
    }
  }

  removeStockwerk() {
    if(this.stockwerke > 1) {
      this.stockwerke--;
      this.toggleEditMode();
    }
  }

  toggleMansarde() {
    this.hasMansarde = !this.hasMansarde;
    this.toggleEditMode();
  }

  toggleKeller() {
    this.hasKeller = !this.hasKeller;
    this.toggleEditMode();
  }

  toggleEditMode(): void {
    toggleEditMode(this.mansardeFlaecheQm, this.showMansardeFlaecheQm);
    toggleEditMode(this.zweiterStockFlaecheQm, this.showZweiterStockFlaecheQm);
    toggleEditMode(this.ersterStockFlaecheQm, this.showErsterStockFlaecheQm);
    toggleEditMode(this.erdgeschossFlaecheQm, this.showErdgeschossFlaecheQm);
    toggleEditMode(this.kellerFlaecheQm, this.showKellerFlaecheQm);
    toggleEditMode(this.wohnkellerFlaecheQm, this.showWohnKellerFlaecheQm, false, false);
  }

  onValueChanged(): void {
    merge(
      this.mansardeFlaecheQm.valueChanges,
      this.zweiterStockFlaecheQm.valueChanges,
      this.ersterStockFlaecheQm.valueChanges,
      this.erdgeschossFlaecheQm.valueChanges,
      this.kellerFlaecheQm.valueChanges,
      this.wohnkellerFlaecheQm.valueChanges
    ).subscribe(() => {
      this.calculateRatePendingEvent.emit();
    });
  }

  getVerbauteFlaeche(): IHvDaten {
    return {
      MansardeFlaecheQm: this.mansardeFlaecheQm.value? Number(this.mansardeFlaecheQm.value): null,
      ZweiterStockFlaecheQm: this.zweiterStockFlaecheQm.value? Number(this.zweiterStockFlaecheQm.value): null,
      ErsterStockFlaecheQm: this.ersterStockFlaecheQm.value? Number(this.ersterStockFlaecheQm.value): null,
      ErdgeschossFlaecheQm: this.erdgeschossFlaecheQm.value? Number(this.erdgeschossFlaecheQm.value): null,
      KellerFlaecheQm: this.kellerFlaecheQm.value? Number(this.kellerFlaecheQm.value): null,
      WohnkellerFlaecheQm: this.wohnkellerFlaecheQm.value? Number(this.wohnkellerFlaecheQm.value): null
    } as IHvDaten;
  }

  setVerbauteFlaeche(daten: IHvDaten) {
    this.erdgeschossFlaecheQm.setValue(daten.ErdgeschossFlaecheQm);

    if(daten.MansardeFlaecheQm > 0){
      this.hasMansarde = true;
      this.mansardeFlaecheQm.setValue(daten.MansardeFlaecheQm);
    }

    if(daten.ZweiterStockFlaecheQm > 0 && daten.ErsterStockFlaecheQm > 0){
      this.stockwerke = 3;
    }else if( daten.ErsterStockFlaecheQm > 0){
      this.stockwerke = 2;
    }

    this.ersterStockFlaecheQm.setValue(daten.ErsterStockFlaecheQm);
    this.zweiterStockFlaecheQm.setValue(daten.ZweiterStockFlaecheQm);

    if(daten.KellerFlaecheQm){
      this.hasKeller = true;
      this.kellerFlaecheQm.setValue(daten.KellerFlaecheQm);
      this.wohnkellerFlaecheQm.setValue(daten.WohnkellerFlaecheQm);
    }

    this.toggleEditMode();
  }
}
